import { useState, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Email, Lock } from '@mui/icons-material';
import { validatePassword, getPasswordRequirementsText } from '@timetable/utils';
import { useAuthStore } from '@/store/authStore';
import { updateUserProfileDirect, changePasswordDirect } from '@timetable/api';
import { useMutation } from '@tanstack/react-query';

export function TeacherSettingsPage() {
  const user = useAuthStore((state) => state.user);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Profile form state (name only)
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Profile update mutation (name only)
  const profileMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      return updateUserProfileDirect({ name: data.name, email: user?.email || '' });
    },
    onSuccess: () => {
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setProfileMessage(null), 5000);
    },
    onError: (error: any) => {
      setProfileMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update profile. Please try again.' 
      });
    },
  });

  // Password change mutation
  const passwordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      return changePasswordDirect(data.currentPassword, data.newPassword);
    },
    onSuccess: () => {
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordMessage(null), 5000);
    },
    onError: (error: any) => {
      setPasswordMessage({ 
        type: 'error', 
        text: error.message || 'Failed to change password. Please try again.' 
      });
    },
  });

  const handleProfileSubmit = (e: FormEvent) => {
    e.preventDefault();
    setProfileMessage(null);
    
    if (!profileForm.name.trim()) {
      setProfileMessage({ type: 'error', text: 'Name is required.' });
      return;
    }
    
    profileMutation.mutate(profileForm);
  };

  const handlePasswordSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    
    if (!passwordForm.currentPassword) {
      setPasswordMessage({ type: 'error', text: 'Current password is required.' });
      return;
    }
    
    if (!passwordForm.newPassword) {
      setPasswordMessage({ type: 'error', text: 'New password is required.' });
      return;
    }
    
    const result = validatePassword(passwordForm.newPassword, passwordForm.confirmPassword);
    if (!result.valid) {
      setPasswordMessage({ type: 'error', text: result.errors.password || result.errors.confirmPassword || '' });
      return;
    }
    
    passwordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Account Settings
      </Typography>

      {/* Profile Settings */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            Profile Information
          </Typography>
          
          {profileMessage && (
            <Alert severity={profileMessage.type} sx={{ mb: 3 }}>
              {profileMessage.text}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleProfileSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={user?.email || ''}
              disabled
              sx={{ mb: 3 }}
              helperText="Email cannot be changed. Contact your administrator if needed."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              disabled={profileMutation.isPending}
              startIcon={profileMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {profileMutation.isPending ? 'Updating...' : 'Update Profile'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Password Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock color="primary" />
            Change Password
          </Typography>
          
          {passwordMessage && (
            <Alert severity={passwordMessage.type} sx={{ mb: 3 }}>
              {passwordMessage.text}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              sx={{ mb: 3 }}
              helperText={getPasswordRequirementsText()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              disabled={passwordMutation.isPending}
              startIcon={passwordMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {passwordMutation.isPending ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}